/**
 * SubscriptionPanel — company plan, billing portal (owner only).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
// navigate used for pricing redirect when checkout required
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../../../api/client'
import { useAuth } from '../../../context/AuthContext'
import { fetchBillingPlan, fetchBillingStatus, openBillingPortal } from '../../../api/billing'

interface SubscriptionSummary {
  subscription_status?: string
  plan_type?:            string
  trial_end_date?:       string
  seats_used?:           number
  seat_limit?:           number
  current_period_end?:   string | null
}

export default function SubscriptionPanel() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOwner = user?.company_role === 'owner'
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const { data: summaryWrap } = useQuery({
    queryKey: ['subscription-summary'],
    queryFn: () => apiFetch<{ data: SubscriptionSummary }>('GET', '/api/subscription/summary'),
    enabled: isOwner,
    staleTime: 60_000,
  })
  const summary = summaryWrap?.data

  const { data: billing } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    enabled: isOwner,
    staleTime: 60_000,
  })

  const { data: plan } = useQuery({
    queryKey: ['billing-plan'],
    queryFn: fetchBillingPlan,
    enabled: isOwner,
    staleTime: 60_000,
  })

  const status = billing?.subscription_status ?? summary?.subscription_status
  const nextCharge = billing?.current_period_end ?? summary?.current_period_end

  async function onManageBilling() {
    if (!isOwner) return
    setBusy(true)
    setErr(null)
    try {
      if (status === 'none' || status === 'locked') {
        navigate('/pricing')
        return
      }
      if (!billing?.current_period_end && status !== 'active' && status !== 'trialing' && status !== 'past_due') {
        navigate('/pricing')
        return
      }
      const url = await openBillingPortal()
      window.location.href = url
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Billing action failed')
      setBusy(false)
    }
  }

  return (
    <div id="billing" className="panel" data-testid="panel-billing" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Subscription &amp; billing</div>
        <span className="panel-st">
          Your Varshyl plan
          {plan ? ` · ${new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency.toUpperCase(), maximumFractionDigits: 0 }).format(plan.amount_cents / 100)}/${plan.interval}` : ''}
        </span>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt" data-testid="billing-plan-status">
            {status === 'active' ? 'Active' : status === 'grace' ? 'Grace period' : status || '—'}
          </div>
          <div className="set-row-st">
            {nextCharge
              ? `Next charge ${new Date(nextCharge).toLocaleDateString()}`
              : status === 'grace' && billing?.grace_days_left != null
                ? `${billing.grace_days_left} days left to add payment`
                : 'Add a payment method to continue'}
          </div>
          {isOwner && summary && (
            <div className="set-row-st" data-testid="subscription-seats">
              {summary.seats_used} of {summary.seat_limit} seats used
            </div>
          )}
        </div>
        <div className="set-row-r">
          {isOwner && (
            <button
              type="button"
              className="btn b-dk btn-sm"
              data-testid="billing-manage-btn"
              disabled={busy}
              onClick={() => void onManageBilling()}
            >
              {busy ? 'Opening…' : status === 'active' ? 'Manage billing' : 'Add payment method'}
            </button>
          )}
        </div>
      </div>
      {err && <p className="set-err">{err}</p>}

      <div className="set-row" style={{ background: '#fafafa' }}>
        <div className="set-row-l">
          <div className="set-row-tt">Soren · your AI assistant</div>
          <div className="set-row-st">Configure training, behavior, and voice for Soren</div>
        </div>
        <div className="set-row-r">
          <button className="btn b-dk btn-sm" data-testid="aria-setup-btn"
            onClick={() => navigate('/settings/aria')}>
            Open Soren setup →
          </button>
        </div>
      </div>
    </div>
  )
}
